import type { z } from 'zod';
import type { LayoutRegistry } from './LayoutRegistry.ts';
import type { SceneCapabilityDescriptor } from './types.ts';
import type {
  VisualConstructRegistry,
} from './VisualConstructRegistry.ts';
import { z as zod } from 'zod';
import { serializeZodSchema } from '../../catalog/ProtocolSchemaRegistry.ts';
import {
  sceneCameraSchema,
  sceneDataViewSchema,
  sceneInteractionSchema,
  sceneNodeTransitionSchema,
  sceneRelationSchema,
  sceneStyleSchema,
  sceneTimelineSchema,
  sceneVisualStateSchema,
} from '../../contracts/index.ts';
import { createBuiltinLayoutRegistry } from './LayoutRegistry.ts';
import {
  createBuiltinVisualConstructRegistry,
} from './VisualConstructRegistry.ts';

export interface SceneCapabilityImplementation {
  kind: 'schema' | 'layout' | 'construct' | 'runtime';
  reference: string;
}

interface SceneCapabilityEntry {
  descriptor: SceneCapabilityDescriptor;
  implementation: SceneCapabilityImplementation;
}

export class SceneCapabilityRegistry {
  private readonly entries = new Map<string, SceneCapabilityEntry>();

  constructor(
    readonly layouts: LayoutRegistry,
    readonly constructs: VisualConstructRegistry,
  ) {}

  register(
    descriptor: SceneCapabilityDescriptor,
    implementation: SceneCapabilityImplementation,
  ): this {
    if (!/^[a-z0-9][a-z0-9.-]+@\d+$/.test(descriptor.key))
      throw new Error(`二维能力 key 非法：${descriptor.key}`);
    if (this.entries.has(descriptor.key))
      throw new Error(`二维能力已经注册：${descriptor.key}`);
    this.entries.set(descriptor.key, {
      descriptor: structuredClone(descriptor),
      implementation: structuredClone(implementation),
    });
    return this;
  }

  resolve(key: string): SceneCapabilityEntry {
    const entry = this.entries.get(key);
    if (!entry)
      throw new Error(`未知二维能力：${key}`);
    return {
      descriptor: structuredClone(entry.descriptor),
      implementation: structuredClone(entry.implementation),
    };
  }

  listDescriptors(): SceneCapabilityDescriptor[] {
    return [...this.entries.values()].map(entry =>
      structuredClone(entry.descriptor),
    );
  }
}

function schemaCapability(
  key: string,
  category: SceneCapabilityDescriptor['category'],
  title: string,
  description: string,
  schema: z.ZodType<unknown>,
  reference: string,
  cost: SceneCapabilityDescriptor['cost'] = 'low',
  examples: SceneCapabilityDescriptor['examples'] = [{}],
): [SceneCapabilityDescriptor, SceneCapabilityImplementation] {
  return [{
    key,
    category,
    title,
    description,
    suitableFor: ['二维教学可视化'],
    inputs: ['结构化 SceneGrammar'],
    outputs: ['NormalizedSceneIR 或声明式运行行为'],
    compatibleWith: ['probfun.scene-grammar@1'],
    configSchema: serializeZodSchema(schema, key),
    constraints: ['禁止函数、脚本、HTML 和任意 PixiJS 代码'],
    determinism: ['相同已校验输入产生等价的 NormalizedSceneIR 或运行行为。'],
    incrementalStrategy: ['稳定 sourceId/runtimeId 驱动局部 reconcile。'],
    diagnostics: ['使用 code、path、relatedIds 和 suggestedFix 返回错误。'],
    cost,
    examples,
    counterExamples: [{ callback: '() => {}' }],
    commonErrors: ['Schema 不合法', '引用未知目标', '超过安全上限'],
    aiGuidance: ['只生成 Descriptor 允许的结构化字段。'],
    repairGuidance: ['根据诊断 path 和 suggestedFix 修改局部配置。'],
  }, {
    kind: 'schema',
    reference,
  }];
}

const primitiveSchema = zod.object({
  primitive: zod.enum([
    'group',
    'rect',
    'rounded-rect',
    'circle',
    'ellipse',
    'line',
    'polyline',
    'polygon',
    'path',
    'arc',
    'star',
    'text',
    'sprite',
    'marker',
  ]),
}).strict();

export function createBuiltinSceneCapabilityRegistry():
SceneCapabilityRegistry {
  const layouts = createBuiltinLayoutRegistry();
  const constructs = createBuiltinVisualConstructRegistry();
  const registry = new SceneCapabilityRegistry(layouts, constructs);

  registry.register(...schemaCapability(
    'probfun.scene.primitive.core@1',
    'primitive',
    '基础二维图元',
    '容器、矢量图形、短文本、受控图片和 marker。',
    primitiveSchema,
    'PixiNodeFactory',
    'low',
    [{ primitive: 'circle' }],
  ));
  registry.register(...schemaCapability(
    'probfun.scene.relation.core@1',
    'relation',
    '空间与连接关系',
    '连接、锚定、对齐、包含、跟随、标签和相对定位。',
    sceneRelationSchema,
    'resolveSceneRelations',
    'low',
    [{
      id: 'edge',
      type: 'connect',
      sourceId: 'source',
      targetId: 'target',
    }],
  ));
  registry.register(...schemaCapability(
    'probfun.scene.style.theme@1',
    'style',
    '主题与视觉变量',
    '安全颜色、线条、透明度、混合和受控滤镜。',
    sceneStyleSchema,
    'resolveSceneStyles',
    'low',
    [{ fill: '#2563eb', opacity: 0.9 }],
  ));
  registry.register(...schemaCapability(
    'probfun.scene.state.core@1',
    'state',
    '统一视觉状态',
    '合并数据、选择、交互、Runtime 和 Timeline 状态。',
    sceneVisualStateSchema,
    'resolveSceneStates',
    'low',
    [{
      id: 'selected',
      source: 'selected',
      priority: 100,
      style: { stroke: '#2563eb', strokeWidth: 3 },
    }],
  ));
  registry.register(...schemaCapability(
    'probfun.scene.timeline.core@1',
    'timeline',
    '声明式时间线',
    'sequence、parallel、stagger、tween、状态、相机和触发。',
    sceneTimelineSchema,
    'SceneTimelineController',
    'medium',
    [{
      id: 'highlight',
      root: {
        id: 'fade',
        type: 'tween',
        targetIds: ['sample'],
        property: 'style.opacity',
        from: 0.4,
        to: 1,
        durationMs: 300,
      },
    }],
  ));
  registry.register(...schemaCapability(
    'probfun.scene.transition.data-join@1',
    'timeline',
    '数据实体进入、更新与退出过渡',
    '稳定数据键对应的节点可声明 enter、update 和 exit；删除节点会保留到退出完成。',
    sceneNodeTransitionSchema,
    'SceneTimelineController.applyReconcileTransitions',
    'low',
    [{
      enter: {
        durationMs: 220,
        visual: { opacity: 0, scale: 0.7, offset: { x: -16, y: 0 } },
      },
      update: {
        durationMs: 240,
        properties: ['transform.x', 'transform.y'],
      },
      exit: {
        durationMs: 180,
        visual: { opacity: 0, scale: 0.7, offset: { x: 16, y: 0 } },
      },
    }],
  ));
  const effectSchema = zod.object({
    property: zod.enum([
      'transform',
      'opacity',
      'color',
      'geometry-progress',
      'number',
    ]),
    easing: zod.enum([
      'linear',
      'ease-in',
      'ease-out',
      'ease-in-out',
      'spring',
      'bounce',
    ]),
  }).strict();
  registry.register(...schemaCapability(
    'probfun.scene.effect.tween@1',
    'effect',
    '受控属性过渡',
    '对 transform、透明度、颜色、几何进度和数值执行声明式过渡。',
    effectSchema,
    'SceneTimelineController.applyProperty',
    'medium',
    [{ property: 'opacity', easing: 'ease-in-out' }],
  ));
  const triggerSchema = zod.object({
    type: zod.enum(['checkpoint', 'trigger', 'interaction', 'runtime']),
    name: zod.string().min(1).max(500),
  }).strict();
  registry.register(...schemaCapability(
    'probfun.scene.trigger.core@1',
    'trigger',
    '教学与交互触发',
    '从时间线、交互或 Runtime 状态发出结构化触发，不执行回调代码。',
    triggerSchema,
    'SceneTimelineController.emitClip',
    'low',
    [{ type: 'checkpoint', name: 'observe-result' }],
  ));
  registry.register(...schemaCapability(
    'probfun.scene.interaction.core@1',
    'interaction',
    '声明式场景交互',
    '指针、拖拽、滚轮、选择、Runtime、Timeline 和相机 action。',
    sceneInteractionSchema,
    'SceneInteractionBridge',
    'low',
    [{
      id: 'select-node',
      event: 'pointertap',
      actions: [{ type: 'selection', mode: 'replace' }],
    }],
  ));
  registry.register(...schemaCapability(
    'probfun.scene.camera.core@1',
    'camera',
    '教学二维相机',
    'fit、focus、follow、pan、zoom、landmark 与坐标转换。',
    sceneCameraSchema,
    'SceneCameraController',
    'low',
    [{ id: 'overview', mode: 'fit-scene' }],
  ));
  registry.register(...schemaCapability(
    'probfun.scene.data-view@1',
    'data-mapping',
    'Scene DataView',
    '字段、筛选、稳定排序、lookup、尺度、量化和格式化。',
    sceneDataViewSchema,
    'resolveSceneDataViews',
    'medium',
    [{ id: 'samples', binding: 'samples', select: ['id', 'value'] }],
  ));

  const assetSchema = zod.object({
    artifactId: zod.string().min(1),
    fit: zod.enum(['contain', 'cover', 'fill']).default('contain'),
  }).strict();
  registry.register(...schemaCapability(
    'probfun.scene.asset.texture@1',
    'asset',
    '受控纹理资源',
    '通过 ArtifactManifest 安全加载、缓存和共享纹理。',
    assetSchema,
    'SceneAssetManager',
    'medium',
    [{ artifactId: 'coin-face', fit: 'contain' }],
  ));

  const textSchema = zod.object({
    text: zod.string().max(1000),
    purpose: zod.enum(['label', 'number', 'short-annotation']),
  }).strict();
  registry.register(...schemaCapability(
    'probfun.scene.text.short@1',
    'text',
    '场景短文本',
    '短标签、动态数值和跟随对象的短注释。',
    textSchema,
    'PixiNodeFactory.Text',
    'low',
    [{ text: '样本均值', purpose: 'label' }],
  ));

  const performanceSchema = zod.object({
    maxRuntimeNodes: zod.number().int().positive().max(50000),
    maxDevicePixelRatio: zod.number().min(1).max(2),
  }).strict();
  registry.register(...schemaCapability(
    'probfun.scene.performance.budget@1',
    'performance',
    '性能预算',
    '统一节点、DPR、布局、路径、资源和 reconcile 上限。',
    performanceSchema,
    'SCENE_LIMITS',
    'low',
    [{ maxRuntimeNodes: 10000, maxDevicePixelRatio: 2 }],
  ));

  const safetySchema = zod.object({
    executableCode: zod.literal(false),
    html: zod.literal(false),
    svgDocument: zod.literal(false),
  }).strict();
  registry.register(...schemaCapability(
    'probfun.scene.safety.closed-world@1',
    'safety',
    '封闭执行边界',
    '只允许注册能力、受控值和结构化命令。',
    safetySchema,
    'sceneGrammarSchema',
    'low',
    [{ executableCode: false, html: false, svgDocument: false }],
  ));

  for (const descriptor of layouts.listDescriptors()) {
    registry.register(descriptor, {
      kind: 'layout',
      reference: descriptor.key,
    });
  }
  for (const descriptor of constructs.listDescriptors()) {
    registry.register(descriptor, {
      kind: 'construct',
      reference: descriptor.key,
    });
  }

  return registry;
}
